/**
 * RealPLC TIA Validation — shared types for the engineering closed-loop.
 *
 * These types are shared between API Gateway and AI Runtime.
 * They are NOT OpenAI/LLM tool definitions — those live in tools/tiaTools.ts.
 */
/**
 * Why a program holding ladder blocks gets no TIA compile yet. A ladder block
 * reaches TIA as SimaticML through Blocks.Import — ladder phase L3, not built —
 * and the SCL external source the gate sends today cannot carry one: TIA's
 * refusal would come back as vendor errors and repair would rewrite the ladder
 * as SCL. The runtime skips with this reason, the gateway refuses with it, and
 * the browser explains it; one value for all three.
 */
export const LADDER_TRANSPORT_UNAVAILABLE = 'ladder_transport_unavailable';
export const DEFAULT_TIA_VALIDATION_SETTINGS = {
    // Project conversations automatically enter the deterministic backend gate.
    // Conversations without a project remain manual because hasProjectContext=false.
    policy: 'preferred',
    agentSelector: { mode: 'any_eligible' },
    autoResumeWhenAgentReady: true,
    validationProfileId: 'siemens-v18-sandbox',
    maxDistinctArtifactValidations: 3,
    maxInfrastructureRetries: 3,
};
// ---------------------------------------------------------------------------
// Codes
// ---------------------------------------------------------------------------
/**
 * The codes the TIA closed loop reports (P2), defined once for the runtime,
 * the gateway and the browser. The Agent's Worker and CloudBridge return the
 * same strings as C# literals, so each value is spelled exactly as its name.
 *
 * One namespace for gate checks, run failures and job results: a code means
 * the same thing wherever it lands. Machine codes only — the words for them
 * live with the readiness table (tiaProject.ts) and the browser.
 */
export const TIA_VALIDATION_CODES = {
    // Which Agent and which project a validation targets (resolveTiaTarget).
    /** An old Agent says TIA Portal is not running on its machine (E1 ③). */
    TIA_NOT_RUNNING: 'TIA_NOT_RUNNING',
    /** Several projects are open and nothing chose one of them (E7 ②). */
    TIA_PROJECT_NOT_SELECTED: 'TIA_PROJECT_NOT_SELECTED',
    /**
     * The chosen project's Agent or registry entry is gone: another machine, a
     * new pairing, a moved or deleted project. Handled as "choose again" (E2).
     */
    TIA_PROJECT_REF_NOT_FOUND: 'TIA_PROJECT_REF_NOT_FOUND',
    /**
     * The target needs an Agent that keeps a project registry: an old Agent
     * with no project open, or asked for a new project (E1 ②).
     */
    AGENT_UPGRADE_REQUIRED: 'AGENT_UPGRADE_REQUIRED',
    /**
     * The Agent is not bound to this RealPLC project. Before P2 the gateway's
     * `evaluateAgent` sent it as a bare literal (api-gateway
     * `agentEligibilityResolver.ts`) and the browser's `tiaSituations.ts` maps
     * that literal; T3 and T4 switch both to this constant.
     */
    AGENT_TARGET_NOT_AUTHORIZED: 'AGENT_TARGET_NOT_AUTHORIZED',
    // Creating and copying projects (E5 ①, E8, E11).
    /** The validation job could not create the new project it was asked for. */
    TIA_PROJECT_CREATE_FAILED: 'TIA_PROJECT_CREATE_FAILED',
    /** The project was saved by a newer TIA Portal than the Worker loads (E8). */
    TIA_PROJECT_VERSION_UNSUPPORTED: 'TIA_PROJECT_VERSION_UNSUPPORTED',
    /** The user's project is open with unsaved changes: save it, then validate. */
    TIA_PROJECT_UNSAVED: 'TIA_PROJECT_UNSAVED',
    /** The Agent-owned project changed in the visible TIA window; preserve the engineer's edits and stop automatic repair. */
    TIA_PROJECT_CHANGED_BY_USER: 'TIA_PROJECT_CHANGED_BY_USER',
    /** Compiler diagnostics were read, but the validation project could not be saved for a safe next attempt. */
    TIA_PROJECT_SAVE_FAILED: 'TIA_PROJECT_SAVE_FAILED',
    /** Window mode could not provide an interactive Agent-owned Portal; never fall back to headless compilation. */
    TIA_WINDOW_UNAVAILABLE: 'TIA_WINDOW_UNAVAILABLE',
    /** Windows refused to start TIA without elevated privileges (Win32 740). */
    TIA_ELEVATION_REQUIRED: 'TIA_ELEVATION_REQUIRED',
    /** The desktop user cancelled the Windows UAC prompt (Win32 1223). */
    TIA_UAC_CANCELLED: 'TIA_UAC_CANCELLED',
    /**
     * Archiving the user's open project into a validation copy failed. Never
     * retried silently: the user may consent, for this one run, to the save-as
     * copy that switches their TIA window to the copy.
     */
    TIA_PROJECT_COPY_FAILED: 'TIA_PROJECT_COPY_FAILED',
    // The program-cycle OB (E6).
    /**
     * Worker result, never a user-facing blocker: the target already holds a
     * RealPLC program-cycle OB under another name (`expectedName`), or its
     * "Main" holds the user's own program (reason `main_has_user_logic`). The
     * gateway renames and resends without spending the attempt budget.
     */
    PROGRAM_OB_CONFLICT: 'PROGRAM_OB_CONFLICT',
    /** A conflict the artifact cannot be renamed out of: it records no alternate name. */
    PROGRAM_OB_CONFLICT_UNRESOLVED: 'PROGRAM_OB_CONFLICT_UNRESOLVED',
    /** The target holds two marked RealPLC program-cycle OBs. */
    PROGRAM_OB_AMBIGUOUS: 'PROGRAM_OB_AMBIGUOUS',
    // The safety floor (E13).
    /** Worker, CloudBridge or gateway refused a request outside the safety floor. */
    TIA_SAFETY_REFUSED: 'TIA_SAFETY_REFUSED',
    // Opening a run's result in TIA (E12, T11).
    /**
     * 「在 TIA 里打开」 asked for a run's validation copy the Agent has cleaned
     * up since: the result is gone, not the project it was made from.
     */
    TIA_VALIDATION_COPY_CLEANED: 'TIA_VALIDATION_COPY_CLEANED',
    /**
     * 「在 TIA 里打开」 asked for a project the user's own TIA Portal has open
     * already: the Agent opens no second window on it.
     */
    TIA_PROJECT_ALREADY_OPEN: 'TIA_PROJECT_ALREADY_OPEN',
    /**
     * The Agent found no TIA Portal on its machine to start, or could not start
     * one to show a project in (tia_start_portal, tia_open_project).
     */
    TIA_PORTAL_NOT_FOUND: 'TIA_PORTAL_NOT_FOUND',
    // Attaching to TIA (T7).
    /**
     * TIA Portal still showed its Openness access prompt when the Agent gave up
     * attaching, after 90 s: someone at that machine allows the Agent's access
     * once, then validates again. It ends a run as blocked, never as a compile
     * failure, and starts no repair.
     */
    TIA_OPENNESS_ACCESS_TIMEOUT: 'TIA_OPENNESS_ACCESS_TIMEOUT',
};
/**
 * The Agent's own failures on a validation job sent for a target, spelled as
 * the Worker and CloudBridge return them. None is about the program: the
 * gateway ends the run on it as blocked, never as a compile failure, and
 * starts no repair. `worker_busy` is not among them: the gateway queues that
 * job again, as for `agent_busy`.
 */
export const TIA_AGENT_ENVIRONMENT_CODES = {
    /** The Agent could not start the TIA Portal it validates in (Worker TargetValidation). */
    portal_start_failed: 'portal_start_failed',
    /** The Agent could not attach to the TIA Portal it started or found (Worker TargetValidation). */
    tia_attach_failed: 'tia_attach_failed',
    /** The Agent could not open the target project, or the copy made from it (Worker TargetValidation). */
    project_open_failed: 'project_open_failed',
    /** The target project holds no PLC to compile the program in (Worker TargetValidation). */
    plc_not_found: 'plc_not_found',
    /** The PLC's software offers no compile (Worker TargetValidation). */
    not_compilable: 'not_compilable',
    /** The Agent could not delete the same-name blocks the program replaces (Worker TargetValidation, E10). */
    same_name_replace_failed: 'same_name_replace_failed',
    /** The Worker stopped on an unexpected error (Worker Program). */
    worker_exception: 'worker_exception',
    /** The Worker returned no result (CloudBridge). */
    empty_worker_result: 'empty_worker_result',
    /** The Agent could not read the program the gateway sent (CloudBridge). */
    invalid_source_bundle: 'invalid_source_bundle',
};
//# sourceMappingURL=tiaValidation.js.map